export const findConversationIndexById = (conversations = [], id = null) =>
  conversations.findIndex(
    (conversation) => String(conversation.id) === String(id)
  );

export const CONVERSATION_READ_STATE_PENDING_TTL_MS = 15000;

export const CONVERSATION_READ_STATE = {
  READ: 'read',
  UNREAD: 'unread',
};

const hasOwn = (object = {}, key = '') =>
  Object.prototype.hasOwnProperty.call(object, key);

const resolveNowMs = (nowMs = () => Date.now()) => {
  const value = typeof nowMs === 'function' ? nowMs() : nowMs;
  return Number.isFinite(Number(value)) ? Number(value) : Date.now();
};

const toComparableTimestamp = (value = null) => {
  if (value === null || value === undefined || value === '') return 0;

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return numericValue > 100000000000
      ? Math.floor(numericValue / 1000)
      : numericValue;
  }

  const parsedValue = Date.parse(value);
  return Number.isFinite(parsedValue) ? Math.floor(parsedValue / 1000) : 0;
};

export const buildConversationUnreadPatch = ({
  lastSeen = undefined,
  unreadCount = 0,
} = {}) => {
  const shouldKeepUnread = lastSeen === null;
  const effectiveUnreadCount = shouldKeepUnread
    ? Math.max(Number(unreadCount) || 0, 1)
    : Number(unreadCount) || 0;

  return {
    agent_last_seen_at: lastSeen,
    unread_count: effectiveUnreadCount,
  };
};

export const isAgentLastSeenUnread = (agentLastSeenAt) =>
  agentLastSeenAt === null || agentLastSeenAt === 0;

export const getConversationReadState = (conversation = null) => {
  if (!conversation || typeof conversation !== 'object') return null;

  if (
    hasOwn(conversation, 'agent_last_seen_at') &&
    conversation.agent_last_seen_at !== undefined
  ) {
    return isAgentLastSeenUnread(conversation.agent_last_seen_at)
      ? CONVERSATION_READ_STATE.UNREAD
      : CONVERSATION_READ_STATE.READ;
  }

  if (hasOwn(conversation, 'unread_count')) {
    return Number(conversation.unread_count) > 0
      ? CONVERSATION_READ_STATE.UNREAD
      : CONVERSATION_READ_STATE.READ;
  }

  return null;
};

export const buildConversationReadStateOperation = ({
  agentLastSeenAt = null,
  id = null,
  nowMs = () => Date.now(),
  ttlMs = CONVERSATION_READ_STATE_PENDING_TTL_MS,
} = {}) => {
  if (id === null || id === undefined || id === '') return null;

  const startedAt = resolveNowMs(nowMs);
  const desiredState = isAgentLastSeenUnread(agentLastSeenAt)
    ? CONVERSATION_READ_STATE.UNREAD
    : CONVERSATION_READ_STATE.READ;

  return {
    id: String(id),
    agent_last_seen_at:
      desiredState === CONVERSATION_READ_STATE.UNREAD ? null : agentLastSeenAt,
    desiredState,
    startedAt,
    expiresAt: startedAt + ttlMs,
  };
};

export const isConversationReadStateOperationActive = ({
  nowMs = () => Date.now(),
  operation = null,
} = {}) => {
  if (!operation || !operation.id || !operation.desiredState) return false;

  return resolveNowMs(nowMs) <= Number(operation.expiresAt || 0);
};

const getIncomingMessageTimestamp = (message = null) => {
  if (!message || typeof message !== 'object') return 0;

  return Math.max(
    toComparableTimestamp(message.created_at),
    toComparableTimestamp(message.timestamp),
    toComparableTimestamp(message.last_activity_at)
  );
};

const isIncomingMessage = (message = null) => {
  const type = message?.message_type ?? message?.message_type_before_type_cast;
  return type === 0 || type === '0' || type === 'incoming';
};

const hasExplicitProviderUnreadState = (conversation = null) => {
  const attributes = conversation?.additional_attributes || {};
  return (
    attributes.gmail_read === false ||
    attributes.microsoft_read === false ||
    attributes.imap_read === false
  );
};

const getLatestIncomingTimestamp = ({
  conversation = null,
  messageCreatedAt = null,
} = {}) => {
  const timestamps = [
    toComparableTimestamp(messageCreatedAt),
    toComparableTimestamp(conversation?.last_incoming_at),
  ];

  const lastMessage = conversation?.last_non_activity_message;
  if (isIncomingMessage(lastMessage)) {
    timestamps.push(getIncomingMessageTimestamp(lastMessage));
  }

  if (Array.isArray(conversation?.messages)) {
    conversation.messages.forEach((message) => {
      if (isIncomingMessage(message)) {
        timestamps.push(getIncomingMessageTimestamp(message));
      }
    });
  }

  return Math.max(...timestamps);
};

const buildPreservedReadPatch = ({
  currentConversation = {},
  operation = null,
} = {}) => {
  const agentLastSeenAt =
    operation?.desiredState === CONVERSATION_READ_STATE.READ
      ? operation.agent_last_seen_at
      : currentConversation.agent_last_seen_at;

  return {
    agent_last_seen_at: agentLastSeenAt,
    unread_count: 0,
  };
};

const buildPreservedUnreadPatch = ({
  currentConversation = {},
  incomingConversation = {},
  operation = null,
} = {}) => ({
  agent_last_seen_at:
    operation?.desiredState === CONVERSATION_READ_STATE.UNREAD
      ? null
      : currentConversation.agent_last_seen_at,
  unread_count: Math.max(
    Number(currentConversation.unread_count) || 0,
    Number(incomingConversation.unread_count) || 0,
    1
  ),
});

const canIncomingUnreadOverrideRead = ({
  currentConversation = null,
  incomingConversation = null,
  messageCreatedAt = null,
  operation = null,
  source = 'generic',
} = {}) => {
  if (
    !operation &&
    (source === 'conversation-read' ||
      hasExplicitProviderUnreadState(incomingConversation))
  ) {
    return true;
  }

  const currentLastSeen = toComparableTimestamp(
    operation?.desiredState === CONVERSATION_READ_STATE.READ
      ? operation.agent_last_seen_at
      : currentConversation?.agent_last_seen_at
  );
  if (!currentLastSeen) return true;

  const latestIncomingTimestamp = getLatestIncomingTimestamp({
    conversation: incomingConversation,
    messageCreatedAt,
  });

  if (latestIncomingTimestamp > currentLastSeen) return true;

  return source === 'incoming-message' && latestIncomingTimestamp === 0;
};

export const resolveConversationReadStateUpdate = ({
  currentConversation = null,
  incomingConversation = null,
  messageCreatedAt = null,
  nowMs = () => Date.now(),
  pendingOperation = null,
  source = 'generic',
} = {}) => {
  if (!currentConversation || !incomingConversation) {
    return incomingConversation;
  }

  const incomingState = getConversationReadState(incomingConversation);
  if (!incomingState) {
    return incomingConversation;
  }

  const currentState = getConversationReadState(currentConversation);
  const hasActiveOperation = isConversationReadStateOperationActive({
    nowMs,
    operation: pendingOperation,
  });

  if (hasActiveOperation && pendingOperation.desiredState !== incomingState) {
    if (
      incomingState === CONVERSATION_READ_STATE.UNREAD &&
      canIncomingUnreadOverrideRead({
        currentConversation,
        incomingConversation,
        messageCreatedAt,
        operation: pendingOperation,
        source,
      })
    ) {
      return incomingConversation;
    }

    const preservedPatch =
      pendingOperation.desiredState === CONVERSATION_READ_STATE.READ
        ? buildPreservedReadPatch({
            currentConversation,
            operation: pendingOperation,
          })
        : buildPreservedUnreadPatch({
            currentConversation,
            incomingConversation,
            operation: pendingOperation,
          });

    return {
      ...incomingConversation,
      ...preservedPatch,
    };
  }

  if (hasActiveOperation && pendingOperation.desiredState === incomingState) {
    return incomingConversation;
  }

  if (
    currentState === CONVERSATION_READ_STATE.READ &&
    incomingState === CONVERSATION_READ_STATE.UNREAD &&
    !canIncomingUnreadOverrideRead({
      currentConversation,
      incomingConversation,
      messageCreatedAt,
      source,
    })
  ) {
    return {
      ...incomingConversation,
      ...buildPreservedReadPatch({ currentConversation }),
    };
  }

  if (
    currentState === CONVERSATION_READ_STATE.UNREAD &&
    incomingState === CONVERSATION_READ_STATE.READ
  ) {
    if (source === 'conversation-read') {
      return incomingConversation;
    }

    return {
      ...incomingConversation,
      ...buildPreservedUnreadPatch({
        currentConversation,
        incomingConversation,
      }),
    };
  }

  return incomingConversation;
};

export const preserveRealtimeUnreadState = ({
  currentConversation = null,
  incomingConversation = null,
  messageCreatedAt = null,
  source = 'generic',
} = {}) => {
  return resolveConversationReadStateUpdate({
    currentConversation,
    incomingConversation,
    messageCreatedAt,
    source,
  });
};

export const applyConversationUnreadState = ({
  conversations = [],
  id = null,
  lastSeen = undefined,
  unreadCount = 0,
} = {}) => {
  const index = findConversationIndexById(conversations, id);
  if (index === -1) {
    return { conversations, index, status: 'missing' };
  }

  const updatedConversation = {
    ...conversations[index],
    ...buildConversationUnreadPatch({ lastSeen, unreadCount }),
  };
  const updatedConversations = conversations.slice();
  updatedConversations.splice(index, 1, updatedConversation);

  return {
    conversations: updatedConversations,
    conversation: updatedConversation,
    index,
    status: 'updated',
  };
};
